import { useEffect, useMemo } from 'react';
import FilterTypes from '@constants/FilterTypes.ts';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import I18n from '@utils/i18n';
import { actionTypes } from '../reducer';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

const useFilters = ({ filters, dispatch }) => {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);
  const onChangeFilter = (fieldName) => (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
  };

  const handleMultiSelectFilterChange = (fieldName) => (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
  };

  const onToggleFilter = (fieldName) => () => {
    dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
  };

  const filtersList = useMemo(() => {
    const list = [
      {
        field: 'name',
        locale: 'name',
        type: 'textBox',
        active: filters.active.name,
        value: filters.values.name,
        onChange: onChangeFilter('name'),
        onToggle: onToggleFilter('name')
      },
      {
        field: 'status',
        locale: 'status',
        type: 'booleanSelect',
        trueLabel: I18n.t('generic.active'),
        falseLabel: I18n.t('generic.inactive'),
        active: filters.active.status,
        value: filters.values.status,
        onChange: onChangeFilter('status'),
        onToggle: onToggleFilter('status')
      },
      {
        field: 'tags',
        backendField: 'tags',
        locale: 'account_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.tags,
        value: filters.values.tags,
        onChange: handleMultiSelectFilterChange('tags'),
        onToggle: onToggleFilter('tags')
      }
    ];

    if (!hasGlobalTerritoriesApplied()) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: handleMultiSelectFilterChange('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    list.push({
      // IP-15560. Options come from /lookup?type=sales_person, which lists only people actually
      // assigned. Active by default: FilterPanel renders only active filters and never calls
      // onToggle, so an inactive filter is unreachable (learned on IP-15509).
      field: 'salesPerson',
      locale: 'sales_person',
      type: FilterTypes.GenericMultiSelect,
      active: filters.active.salesPerson,
      value: filters.values.salesPerson,
      onChange: handleMultiSelectFilterChange('salesPerson'),
      onToggle: onToggleFilter('salesPerson')
    });

    return list;
  }, [filters, dispatch, globalTerritoriesApplied]);

  return filtersList;
};

export default useFilters;
